// Flush the chunk cache's .state snapshot on tab-close,
// tab-hidden, and a 30-second idle backstop.
//
// Runs on the main thread because browser lifecycle events don't
// propagate into dedicated Workers. Each trigger Comlink-calls
// worker.cacheFlushState(handle), which is a no-op when no cache
// is installed for that vault.

import { useEffect } from "react";
import { useVault } from "./useVault";
import { useSession } from "../stores/session";

const IDLE_FLUSH_MS = 30_000;

export function useCacheFlushLifecycle(): void {
  const vault = useVault();
  const handle = useSession((s) => s.session?.handle ?? null);

  useEffect(() => {
    if (handle === null) return;

    const flush = (): void => {
      void vault.cacheFlushState(handle).catch(() => {
        // Best-effort; ignore failures (e.g. handle died mid-flush).
      });
    };

    const onVisibility = (): void => {
      if (document.visibilityState === "hidden") flush();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    const interval = window.setInterval(flush, IDLE_FLUSH_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
      window.clearInterval(interval);
    };
  }, [vault, handle]);
}
